import React, { useState, useEffect } from "react";
import { useParams} from "react-router-dom";
import { useManualQuery } from "graphql-hooks";
import { isValidUUIDv4 } from "../hooks/uuid";
import { Box, Typography, Rating, Tooltip } from "@mui/material";
import {
  LocalShippingOutlined,
  AccessTimeOutlined,
  EmailOutlined,
  PhoneOutlined,
  CakeOutlined,
  LocationOnOutlined,
  PublicOutlined,
} from "@mui/icons-material";
import PublicPageLayout from "./PublicPageLayout";
import mascotIcon from "../assets/icons/mascotIcon.png";
import FloatingMenu from "../components/FloatingMenu";
import AlertPopup from "../components/AlertPopup";

import { USER_DATA_BY_UUID } from "../graphQL/user_qry";

const IconWrapper = ({ children, color = "#666" }) => (
  <Box
    sx={{
      width: 38,
      height: 38,
      borderRadius: "50%",
      backgroundColor: "#FAFAFA",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </Box>
);
//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);
const PublicProfile = () => {
  const { userUuid } = useParams();
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [usrDataObj, setUsrDataObj] = useState({});
  const [warningPopup, setWarningPopup] = useState({
    open: false,    
  });
  const [getUsrData] = useManualQuery(USER_DATA_BY_UUID);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {      
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return ""; 
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const setUserData = async () => {
    if (!userUuid) return;
    if (isValidUUIDv4(userUuid)){  
        const {
          loading: loadingUsrData,
          error: errorCurrUsrData,
          data: usrData,
        } = await getUsrData({
          variables: {
            uuid: userUuid,
          },
        });
        if (!loadingUsrData && usrData) {
          setUsrDataObj(usrData.userDataByUuid);
          console.log("UserData",usrData.userDataByUuid);          
        } else {
          console.log("errorCurrUsrData: ", errorCurrUsrData);
        }
    } else {
      setWarningPopup({
        open: true,
        title: "This URL is not valid.",
      });
    }
  };
  
  useEffect(() => {
    (async () => setUserData())();
  }, [userUuid]);  

  return (
    <PublicPageLayout 
      activeMenuItem="profile"
      userUuid={userUuid}
    >
      {/* About Me Section */}
      <Box
        sx={{
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box p={2}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            About Me
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.6, mb: 3 }}
          >
            {usrDataObj.bio}
          </Typography>

          {/* Overall Rating */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Overall Rating
            </Typography>
            <Box
              sx={{
                width: "1px",
                height: "20px",
                backgroundColor: "#e0e0e0",
                opacity: 0.6,
              }}
            />
            <StyledTooltip title={`${usrDataObj.ratingNo} of 5`}>
              <Box sx={{ display: "inline-block" }}>
                <Rating
                  value={Number(usrDataObj.ratingNo)}
                  precision={0.5}
                  readOnly
                  size="small"
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    "& .MuiRating-iconFilled": {
                      color: "#E53935",
                    },
                  }}
                />
              </Box>
            </StyledTooltip>
          </Box>

          {/* Total Ratings */}
          <Box>
            <Typography variant="body2" sx={{ mb: 3, fontWeight: 600 }}>
              Total No of Ratings: {usrDataObj.ratingCount}
            </Typography>
          </Box>

          {/* Ships In Days */}
          {usrDataObj.shipsIn && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconWrapper>
                  <LocalShippingOutlined sx={{ color: "#666", fontSize: 20 }} />
                </IconWrapper>
                <Typography variant="body2" color="text.secondary">
                  Ships in (Days)
                </Typography>
              </Box>
              <Typography variant="body2">
                {usrDataObj.shipsIn ? usrDataObj.shipsIn : "N/A" } Days
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Personal Information Section */}
      <Box pt={1}>
        <Box p={2} pb={0}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Personal Information
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, lineHeight: 1.6 }}
          >
            You can't view certain details because they're private and locked
            by the user.
          </Typography>

          {/* Date Joined */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              py: 1,
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconWrapper>
                <img src={mascotIcon} alt="Mascot" />
              </IconWrapper>
              <Typography variant="body2" color="text.secondary">
                Date Joined
              </Typography>
            </Box>
            <Typography variant="body2">{formatDate(usrDataObj.dateJoined)}</Typography>
          </Box>          
        </Box>
      </Box>

      {/* Email */}
      {usrDataObj.emailVisibility && (
      <Box
        sx={{
          py: 2,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconWrapper>
              <EmailOutlined sx={{ color: "#666", fontSize: 20 }} />
            </IconWrapper>
            <Typography variant="body2" color="text.secondary">
              Email
            </Typography>
          </Box>
          <Typography variant="body2">{usrDataObj.email}</Typography>
        </Box>
      </Box>
      )}

      {/* Phone No */}
      {usrDataObj.phoneNoVisibility && (
      <Box
        sx={{
          py: 2,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconWrapper>
              <PhoneOutlined sx={{ color: "#666", fontSize: 20 }} />
            </IconWrapper>
            <Typography variant="body2" color="text.secondary">
              Phone No
            </Typography>
          </Box>
          <Typography variant="body2">{usrDataObj.phoneNo}</Typography>
        </Box>
      </Box>
      )}

      {/* Date of Birth */}
      <Box
        sx={{
          py: 2,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconWrapper>
              <CakeOutlined sx={{ color: "#666", fontSize: 20 }} />
            </IconWrapper>
            <Typography variant="body2" color="text.secondary">
              Date Of Birth
            </Typography>
          </Box>
          <Typography variant="body2">{formatDate(usrDataObj.birthDate)}</Typography>
        </Box>
      </Box>

      {/* Address */}
      {usrDataObj.addressVisibility && (
      <Box
        sx={{
          py: 1,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconWrapper>
              <LocationOnOutlined sx={{ color: "#666", fontSize: 20 }} />
            </IconWrapper>
            <Typography variant="body2" color="text.secondary">
              Address
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              textAlign: "right",
              gap: 1,
            }}
          >
            <Typography variant="body2">{usrDataObj.address}</Typography>
            <Typography variant="body2">
              {`${usrDataObj.city ?? ""}${
                usrDataObj.city || usrDataObj.state ? ", " : ""
              }${usrDataObj.state ?? ""} ${
                usrDataObj.zip ?? ""
              }`.trim()}
            </Typography>
          </Box>
        </Box>
      </Box>
      )}

      {/* Country */}
      <Box
        sx={{
          py: 2,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconWrapper>
              <PublicOutlined sx={{ color: "#666", fontSize: 20 }} />
            </IconWrapper>
            <Typography variant="body2" color="text.secondary">
              Country
            </Typography>
          </Box>
          <Typography variant="body2">{usrDataObj.country}</Typography>
        </Box>
      </Box>
      
      <AlertPopup
          open={warningPopup.open}
          title={warningPopup.title}
          message=""
          confirmText="OK"
          cancelText= ""
          onConfirm={() => setWarningPopup(false)}
          onClose={() => setWarningPopup(false)}
        />
        
      <FloatingMenu left={floatMenuLeftMargin} />
    </PublicPageLayout>
  );
};

export default PublicProfile;
